/**
 * BookClub — global discussion board with auto-listed author sub-boards.
 *
 * Layout (responsive):
 *   • Desktop: left rail (boards) · main pane (post list / thread)
 *   • Mobile : top board selector pill row · stacked feed below
 *
 * Active state for current board is held in `useState` (no URL routing
 * for the side rail — keeps deep-links simple).
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  MessageCircle, ChevronLeft, Heart, Trash2, Pin, Send, Loader2, Users, Plus, Megaphone,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import toast from 'react-hot-toast';
import AdminAnnouncementComposer from '../components/AdminAnnouncementComposer';
import { FlashReadButton } from '../components/FlashReadOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtRel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
};

const BookClub = () => {
  const [user, setUser] = useState(null);
  const [boards, setBoards] = useState([]);
  const [activeBoard, setActiveBoard] = useState('general');
  const [posts, setPosts] = useState([]);
  const [openPost, setOpenPost] = useState(null); // { post, replies }
  const [composeBody, setComposeBody] = useState('');
  const [composeTitle, setComposeTitle] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/auth/me`);
        if (r.ok) setUser(await r.json());
      } catch { /* ignore */ }
    })();
  }, []);

  const loadBoards = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/boards`);
      if (r.ok) {
        const d = await r.json();
        setBoards(d.boards || []);
      }
    } catch { /* ignore */ }
  }, []);

  const loadPosts = useCallback(async (board) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts?board=${encodeURIComponent(board)}`);
      if (r.ok) {
        const d = await r.json();
        setPosts(d.posts || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadBoards(); }, [loadBoards]);
  useEffect(() => { loadPosts(activeBoard); }, [activeBoard, loadPosts]);

  const openThread = async (postId) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts/${postId}`);
      if (r.ok) {
        setOpenPost(await r.json());
        setReplyBody('');
      }
    } catch { /* ignore */ }
  };

  const submitPost = async (e) => {
    e.preventDefault();
    if (!composeBody.trim()) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board: activeBoard,
          title: composeTitle.trim(),
          body: composeBody.trim(),
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success('Posted');
      setComposeBody('');
      setComposeTitle('');
      loadPosts(activeBoard);
      loadBoards();
    } catch (err) {
      toast.error(`Failed: ${err.message || err}`);
    }
    setBusy(false);
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!openPost || !replyBody.trim()) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts/${openPost.post.post_id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: replyBody.trim() }),
      });
      if (!r.ok) throw new Error(await r.text());
      setReplyBody('');
      openThread(openPost.post.post_id);
    } catch (err) {
      toast.error(`Failed: ${err.message || err}`);
    }
    setBusy(false);
  };

  const toggleLike = async (post) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts/${post.post_id}/like`, { method: 'POST' });
      if (r.ok) {
        const d = await r.json();
        setPosts((prev) => prev.map((p) => p.post_id === post.post_id ? { ...p, like_count: d.like_count } : p));
        if (openPost?.post.post_id === post.post_id) {
          setOpenPost({ ...openPost, post: { ...openPost.post, like_count: d.like_count } });
        }
      }
    } catch { /* ignore */ }
  };

  const canDeletePost = (p) => user && (p.author_user_id === user.user_id || user.role === 'admin');

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await authFetch(`${BACKEND_URL}/api/book-club/posts/${id}`, { method: 'DELETE' });
      setOpenPost(null);
      loadPosts(activeBoard);
      loadBoards();
    } catch { /* ignore */ }
  };
  const deleteReply = async (id) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await authFetch(`${BACKEND_URL}/api/book-club/replies/${id}`, { method: 'DELETE' });
      if (openPost) openThread(openPost.post.post_id);
    } catch { /* ignore */ }
  };

  const togglePin = async (id) => {
    try {
      await authFetch(`${BACKEND_URL}/api/book-club/posts/${id}/pin`, { method: 'POST' });
      loadPosts(activeBoard);
    } catch { /* ignore */ }
  };

  const activeLabel = boards.find((b) => b.id === activeBoard)?.label || 'General';

  // ─── Thread view ───
  if (openPost) {
    return (
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4">
        <button
          type="button"
          onClick={() => setOpenPost(null)}
          className="mb-3 inline-flex items-center gap-1 text-sm text-indigo-300 hover:text-indigo-200"
          data-testid="thread-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back to {activeLabel}
        </button>
        <div className="bg-slate-800/70 border border-slate-700 rounded-2xl p-4">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              {openPost.post.title && (
                <h1 className="text-lg font-bold text-white mb-1">{openPost.post.title}</h1>
              )}
              <p className="text-xs text-gray-400 mb-2">
                <span className="font-semibold text-indigo-200">{openPost.post.author_name}</span>
                {' · '}{fmtRel(openPost.post.created_at)}
              </p>
              <p className="text-sm text-gray-100 whitespace-pre-line">{openPost.post.body}</p>
              <div className="flex items-center gap-3 mt-3">
                {/* Flash-read this thread (Feb 9 2026 user spec — make
                    flash reading available across all reading surfaces).
                    Self-hides on short posts. */}
                <FlashReadButton
                  text={openPost.post.body || ''}
                  title={openPost.post.title || 'Book Club thread'}
                  testid="bookclub-thread-flash-btn"
                />
                <button
                  type="button"
                  onClick={() => toggleLike(openPost.post)}
                  className="inline-flex items-center gap-1 text-xs text-pink-300 hover:text-pink-200"
                  data-testid="thread-like"
                >
                  <Heart className="w-4 h-4" /> {openPost.post.like_count || 0}
                </button>
                {user?.role === 'admin' && (
                  <button
                    type="button"
                    onClick={() => togglePin(openPost.post.post_id)}
                    className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"
                    data-testid="thread-pin"
                  >
                    <Pin className="w-4 h-4" /> {openPost.post.pinned ? 'Unpin' : 'Pin'}
                  </button>
                )}
                {canDeletePost(openPost.post) && (
                  <button
                    type="button"
                    onClick={() => deletePost(openPost.post.post_id)}
                    className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"
                    data-testid="thread-delete"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {openPost.replies.map((r) => (
            <div key={r.reply_id} className="bg-slate-800/40 border border-slate-700 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-gray-400 mb-1">
                    <span className="font-semibold text-indigo-200">{r.author_name}</span>
                    {' · '}{fmtRel(r.created_at)}
                  </p>
                  <p className="text-sm text-gray-100 whitespace-pre-line">{r.body}</p>
                </div>
                {user && (r.author_user_id === user.user_id || user.role === 'admin') && (
                  <button
                    type="button"
                    onClick={() => deleteReply(r.reply_id)}
                    className="w-7 h-7 rounded-md text-gray-400 hover:text-red-300 hover:bg-red-500/10 flex items-center justify-center"
                    data-testid={`reply-delete-${r.reply_id}`}
                    aria-label="Delete reply"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submitReply} className="mt-4 sticky bottom-2 flex items-end gap-2 bg-slate-900/90 backdrop-blur-md p-2 rounded-2xl border border-slate-700">
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder="Write a reply…"
            rows={2}
            maxLength={2000}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-indigo-400 focus:outline-none resize-none"
            data-testid="reply-body"
          />
          <button
            type="submit"
            disabled={busy || !replyBody.trim()}
            className="h-10 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold disabled:opacity-50 flex items-center gap-1"
            data-testid="reply-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Reply
          </button>
        </form>
      </div>
    );
  }

  // ─── Board list view ───
  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-indigo-300" />
          <h1 className="text-xl sm:text-2xl font-bold text-white">Book Club</h1>
        </div>
        {user?.role === 'admin' && (
          <button
            type="button"
            onClick={() => setAnnounceOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-fuchsia-400/50 text-fuchsia-200 hover:bg-fuchsia-500/20 active:bg-fuchsia-500/30 transition-colors"
            data-testid="bookclub-announce-btn"
            title="Broadcast announcement"
          >
            <Megaphone className="w-3.5 h-3.5" />
            Announce
          </button>
        )}
      </div>

      <AdminAnnouncementComposer
        open={announceOpen}
        onClose={() => setAnnounceOpen(false)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Board rail */}
        <aside className="lg:col-span-1">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-2">
            <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 px-2 pt-1 pb-2">Boards</p>
            <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              {boards.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => { setActiveBoard(b.id); setOpenPost(null); }}
                  className={`text-left px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    activeBoard === b.id
                      ? 'bg-indigo-500/25 border border-indigo-400/50 text-white'
                      : 'hover:bg-slate-800 text-gray-300 border border-transparent'
                  }`}
                  data-testid={`board-${b.id}`}
                >
                  <div className="flex items-center gap-2">
                    <MessageCircle className={`w-4 h-4 ${b.scope === 'general' ? 'text-indigo-300' : 'text-fuchsia-300'}`} />
                    <span className="truncate">{b.label}</span>
                  </div>
                  <span className="block text-[10px] text-gray-500 mt-0.5">
                    {b.post_count} {b.post_count === 1 ? 'post' : 'posts'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Feed */}
        <main className="lg:col-span-3">
          {/* Compose */}
          <form
            onSubmit={submitPost}
            className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 mb-4"
            data-testid="board-compose"
          >
            <p className="text-xs text-gray-400 mb-2">
              Posting to <span className="font-semibold text-indigo-200">{activeLabel}</span>
            </p>
            <input
              type="text"
              value={composeTitle}
              onChange={(e) => setComposeTitle(e.target.value)}
              placeholder="Title (optional)"
              maxLength={160}
              className="w-full px-3 py-2 mb-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm focus:border-indigo-400 focus:outline-none"
              data-testid="board-compose-title"
            />
            <textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              placeholder="What did you think? What did you read? Recommend a book…"
              rows={3}
              maxLength={4000}
              className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm focus:border-indigo-400 focus:outline-none resize-none"
              data-testid="board-compose-body"
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={busy || !composeBody.trim()}
                className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="board-compose-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Post
              </button>
            </div>
          </form>

          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              No posts yet in <span className="text-white">{activeLabel}</span>. Be the first!
            </div>
          ) : (
            <ul className="space-y-2">
              {posts.map((p) => (
                <li
                  key={p.post_id}
                  className={`bg-slate-800/50 hover:bg-slate-800 border ${
                    p.pinned ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-700'
                  } rounded-xl p-3 cursor-pointer transition-colors`}
                  onClick={() => openThread(p.post_id)}
                  data-testid={`post-row-${p.post_id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      {p.pinned && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-amber-300 mb-1">
                          <Pin className="w-3 h-3" /> Pinned
                        </span>
                      )}
                      {p.title && (
                        <h3 className="text-sm sm:text-base font-bold text-white truncate">{p.title}</h3>
                      )}
                      <p className="text-xs text-gray-400">
                        <span className="font-semibold text-indigo-200">{p.author_name}</span>
                        {' · '}{fmtRel(p.created_at)}
                      </p>
                      <p className="text-sm text-gray-200 mt-1 line-clamp-2">{p.body}</p>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle className="w-3.5 h-3.5" /> {p.reply_count || 0}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleLike(p); }}
                          className="inline-flex items-center gap-1 hover:text-pink-300"
                          data-testid={`post-like-${p.post_id}`}
                        >
                          <Heart className="w-3.5 h-3.5" /> {p.like_count || 0}
                        </button>
                        <FlashReadButton
                          text={p.body || ''}
                          title={p.title || 'Book Club thread'}
                          testid={`post-flash-${p.post_id}`}
                          label="Flash"
                        />
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </main>
      </div>
    </div>
  );
};

export default BookClub;
